import React, { useEffect, useState } from 'react';
import { getClientes, createCliente, updateCliente, updateClienteEstado } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { Plus, Search, Users, Phone, MapPin, Edit, MoreVertical } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { useAuth } from '../context/AuthContext';

const estadoColors = {
    activo: 'bg-accent/10 text-accent border-accent/20',
    bloqueado: 'bg-destructive/10 text-destructive border-destructive/20',
    moroso_cronico: 'bg-warning/10 text-warning border-warning/20',
};

const estadoLabels = {
    activo: 'Activo',
    bloqueado: 'Bloqueado',
    moroso_cronico: 'Moroso Crónico',
};

export default function ClientesPage() {
    const { hasRole } = useAuth();
    const [clientes, setClientes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingCliente, setEditingCliente] = useState(null);
    const [form, setForm] = useState({
        cedula: '',
        nombre: '',
        direccion: '',
        zona: '',
        telefono: '',
        telefono_alterno: '',
        referencia: '',
        email: '',
    });

    useEffect(() => {
        loadClientes();
    }, []);

    const loadClientes = async (searchTerm = '') => {
        try {
            const params = searchTerm ? { search: searchTerm } : {};
            const { data } = await getClientes(params);
            setClientes(data);
        } catch (error) {
            toast.error('Error al cargar clientes');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        loadClientes(search);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingCliente) {
                await updateCliente(editingCliente.id, form);
                toast.success('Cliente actualizado');
            } else {
                await createCliente(form);
                toast.success('Cliente creado');
            }
            setDialogOpen(false);
            resetForm();
            loadClientes();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error al guardar cliente');
        }
    };

    const handleEdit = (cliente) => {
        setEditingCliente(cliente);
        setForm({
            cedula: cliente.cedula,
            nombre: cliente.nombre,
            direccion: cliente.direccion,
            zona: cliente.zona,
            telefono: cliente.telefono,
            telefono_alterno: cliente.telefono_alterno || '',
            referencia: cliente.referencia || '',
            email: cliente.email || '',
        });
        setDialogOpen(true);
    };

    const handleEstado = async (clienteId, estado) => {
        try {
            await updateClienteEstado(clienteId, estado);
            toast.success(`Estado actualizado a ${estadoLabels[estado]}`);
            loadClientes();
        } catch (error) {
            toast.error('Error al actualizar estado');
        }
    };

    const resetForm = () => {
        setEditingCliente(null);
        setForm({
            cedula: '',
            nombre: '',
            direccion: '',
            zona: '',
            telefono: '',
            telefono_alterno: '',
            referencia: '',
            email: '',
        });
    };

    const openNewDialog = () => {
        resetForm();
        setDialogOpen(true);
    };

    if (loading) {
        return (
            <div className="space-y-6">
                <Skeleton className="h-10 w-48" />
                <Skeleton className="h-96" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="clientes-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="font-heading text-2xl sm:text-3xl font-bold">Clientes</h1>
                    <p className="text-muted-foreground mt-1">{clientes.length} clientes registrados</p>
                </div>
                {hasRole(['admin', 'cajero', 'supervisor']) && (
                    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                        <DialogTrigger asChild>
                            <Button onClick={openNewDialog} data-testid="new-cliente-btn">
                                <Plus className="w-4 h-4 mr-2" />
                                Nuevo Cliente
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-lg">
                            <DialogHeader>
                                <DialogTitle className="font-heading">
                                    {editingCliente ? 'Editar Cliente' : 'Nuevo Cliente'}
                                </DialogTitle>
                            </DialogHeader>
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="cedula">Cédula/ID</Label>
                                        <Input
                                            id="cedula"
                                            value={form.cedula}
                                            onChange={(e) => setForm({ ...form, cedula: e.target.value })}
                                            required
                                            disabled={!!editingCliente}
                                            data-testid="cliente-cedula-input"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="telefono">Teléfono</Label>
                                        <Input
                                            id="telefono"
                                            value={form.telefono}
                                            onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                                            required
                                            data-testid="cliente-telefono-input"
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="nombre">Nombre Completo</Label>
                                    <Input
                                        id="nombre"
                                        value={form.nombre}
                                        onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                                        required
                                        data-testid="cliente-nombre-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="direccion">Dirección</Label>
                                    <Input
                                        id="direccion"
                                        value={form.direccion}
                                        onChange={(e) => setForm({ ...form, direccion: e.target.value })}
                                        required
                                        data-testid="cliente-direccion-input"
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="zona">Zona/Barrio</Label>
                                        <Input
                                            id="zona"
                                            value={form.zona}
                                            onChange={(e) => setForm({ ...form, zona: e.target.value })}
                                            required
                                            data-testid="cliente-zona-input"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="telefono_alterno">Tel. Alterno</Label>
                                        <Input
                                            id="telefono_alterno"
                                            value={form.telefono_alterno}
                                            onChange={(e) => setForm({ ...form, telefono_alterno: e.target.value })}
                                        />
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="email">Email</Label>
                                        <Input
                                            id="email"
                                            type="email"
                                            value={form.email}
                                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="referencia">Referencia</Label>
                                        <Input
                                            id="referencia"
                                            value={form.referencia}
                                            onChange={(e) => setForm({ ...form, referencia: e.target.value })}
                                        />
                                    </div>
                                </div>
                                <div className="flex justify-end gap-2 pt-4">
                                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                                        Cancelar
                                    </Button>
                                    <Button type="submit" data-testid="cliente-submit-btn">
                                        {editingCliente ? 'Actualizar' : 'Crear'}
                                    </Button>
                                </div>
                            </form>
                        </DialogContent>
                    </Dialog>
                )}
            </div>

            {/* Search */}
            <Card>
                <CardContent className="p-4">
                    <form onSubmit={handleSearch} className="flex gap-2">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                                placeholder="Buscar por nombre o cédula..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="pl-10"
                                data-testid="cliente-search-input"
                            />
                        </div>
                        <Button type="submit" data-testid="cliente-search-btn">Buscar</Button>
                    </form>
                </CardContent>
            </Card>

            {/* Table */}
            <Card>
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Cliente</TableHead>
                                    <TableHead className="hidden md:table-cell">Contacto</TableHead>
                                    <TableHead className="hidden lg:table-cell">Zona</TableHead>
                                    <TableHead>Estado</TableHead>
                                    <TableHead className="w-12"></TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {clientes.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5} className="text-center py-12">
                                            <Users className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
                                            <p className="text-muted-foreground">No hay clientes registrados</p>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    clientes.map((cliente) => (
                                        <TableRow key={cliente.id} className="animate-fadeIn">
                                            <TableCell>
                                                <div>
                                                    <p className="font-medium">{cliente.nombre}</p>
                                                    <p className="text-sm text-muted-foreground font-mono">{cliente.cedula}</p>
                                                </div>
                                            </TableCell>
                                            <TableCell className="hidden md:table-cell">
                                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                                    <Phone className="w-4 h-4" />
                                                    {cliente.telefono}
                                                </div>
                                            </TableCell>
                                            <TableCell className="hidden lg:table-cell">
                                                <div className="flex items-center gap-2 text-sm">
                                                    <MapPin className="w-4 h-4 text-muted-foreground" />
                                                    {cliente.zona}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant="outline" className={estadoColors[cliente.estado]}>
                                                    {estadoLabels[cliente.estado]}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <Button variant="ghost" size="icon" data-testid={`cliente-menu-${cliente.id}`}>
                                                            <MoreVertical className="w-4 h-4" />
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end">
                                                        <DropdownMenuItem onClick={() => handleEdit(cliente)}>
                                                            <Edit className="w-4 h-4 mr-2" />
                                                            Editar
                                                        </DropdownMenuItem>
                                                        {hasRole(['admin', 'supervisor']) && (
                                                            <>
                                                                <DropdownMenuItem onClick={() => handleEstado(cliente.id, 'activo')}>
                                                                    Marcar Activo
                                                                </DropdownMenuItem>
                                                                <DropdownMenuItem onClick={() => handleEstado(cliente.id, 'bloqueado')}>
                                                                    Bloquear
                                                                </DropdownMenuItem>
                                                                <DropdownMenuItem onClick={() => handleEstado(cliente.id, 'moroso_cronico')}>
                                                                    Marcar Moroso Crónico
                                                                </DropdownMenuItem>
                                                            </>
                                                        )}
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
